import { Query } from "@apollo/client/react/components";
import React from "react";
import { useSelfie } from "~/components/auth/Auth";
import CompanyAccountBillingInfoQuery from "~/lib/queries/CompanyAccountBillingInfoQuery";
import {
  ICompanyAccountBillingInfoQuery,
  ICompanyAccountBillingInfoQueryVariables,
} from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import Redirect from "~/lib/route/Redirect";
import CurrentPlanAndUsage from "~/components/makers/CurrentPlanAndUsage";
import PaymentHistory from "~/components/makers/PaymentHistory";
import CurrentBilling from "~/components/makers/CurrentBilling";
import VendorSponsorings from "~/components/makers/VendorSponsorings";
import { Margin } from "~/components/spacings";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import { IVendorUser } from "~/types/types";

interface ICompanyAccountBillingInfoProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}

export default function CompanyAccountBillingInfo({ vendor }: ICompanyAccountBillingInfoProps) {
  const user = useSelfie();
  const isMobile = useIsMobile();

  if (!canAccessBillingInfo(user, vendor.usersCanAccessAccountPage, vendor.viewerIsMember)) {
    return <Redirect to="/" />;
  }

  return (
    <Query<ICompanyAccountBillingInfoQuery, ICompanyAccountBillingInfoQueryVariables>
      query={CompanyAccountBillingInfoQuery}
      variables={{
        slug: (user as IVendorUser).company!.slug,
      }}
    >
      {({ data, loading }) => {
        if (loading) {
          return null;
        }

        if (!data || !data.company) {
          return null;
        }

        const { company } = data;

        return (
          <div
            css={{
              width: "100%",
            }}
          >
            <CurrentPlanAndUsage company={company} />

            <Margin mt={20}>
              <CurrentBilling company={company} />
            </Margin>

            <Margin mt={20}>
              <VendorSponsorings company={company} />
            </Margin>

            {isMobile ? null : (
              <Margin mt={20}>
                <PaymentHistory company={company} />
              </Margin>
            )}
          </div>
        );
      }}
    </Query>
  );
}

function canAccessBillingInfo(
  user: ISelfieQuery["selfie"] | null,
  usersCanAccessAccountPage: boolean,
  viewerIsMember: boolean | null,
) {
  if (!user) {
    return false;
  }

  if (user.__typename !== "VendorUser") {
    return false;
  }

  if (!user.company) {
    return false;
  }

  if (user.role !== "COMPANY_OWNER" && (!usersCanAccessAccountPage || !viewerIsMember)) {
    return false;
  }

  return true;
}
